import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LOG_SOURCES,
  LOG_SOURCE_DESCRIPTIONS,
  LOG_SOURCE_LABELS,
  PACKAGE_EVENT_TYPES,
  type LogSource,
  type SystemLogEntry,
  type SystemLogFilterOptions,
} from '@azko/shared';

import { api } from '../../lib/api.js';
import { LogFeed, describeLogAction } from '../../components/LogFeed.js';
import {
  Button,
  Card,
  EmptyState,
  LoadingRows,
  SearchInput,
  Select,
  cx,
} from '../../components/ui.js';

/**
 * Os logs do sistema inteiro.
 *
 * Antes esta tela mostrava so `platform_audit_log` — o que a EQUIPE Azko fez.
 * Quem atendia um cliente via metade da historia: "cortamos o acesso em 12/08"
 * aparecia, "o sindico apagou o porteiro da noite" nao, e "a encomenda do 101
 * foi entregue as 14h" muito menos. As tres trilhas existiam; faltava uma tela
 * que as lesse juntas.
 *
 * **Nada vem ligado por padrao alem de sete dias.** Um admin que abre a tela ve
 * a semana inteira do sistema; quem investiga aperta os filtros. O contrario —
 * abrir com tudo desde sempre — leria a base inteira para mostrar trinta
 * linhas, e ainda entregaria ruido em vez de resposta.
 */

interface LogsResponse {
  items: SystemLogEntry[];
  nextCursor: string | null;
  /** So vem na primeira pagina; nas seguintes a tela ja tem. */
  filters: SystemLogFilterOptions | null;
}

const PERIODS = [
  { value: '1', label: 'Ultimas 24 horas' },
  { value: '7', label: 'Ultimos 7 dias' },
  { value: '30', label: 'Ultimos 30 dias' },
  { value: '90', label: 'Ultimos 90 dias' },
  { value: 'todos', label: 'Desde o comeco' },
];

export function AuditPage() {
  const [sources, setSources] = useState<LogSource[]>([]);
  const [condominium, setCondominium] = useState('');
  const [action, setAction] = useState('');
  const [days, setDays] = useState('7');
  const [search, setSearch] = useState('');
  const [pages, setPages] = useState<string[]>([]);

  const before = pages[pages.length - 1] ?? null;
  // Busca de uma letra so nao filtra nada e custa uma varredura por LIKE em
  // tres tabelas; o backend recusa, e a tela nem chega a pedir.
  const effectiveSearch = search.trim().length >= 2 ? search.trim() : '';

  /** Trocar qualquer filtro volta para a primeira pagina: o cursor pertencia ao
   *  conjunto anterior e continuaria paginando a lista antiga. */
  function reset<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setPages([]);
    };
  }

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin', 'logs', sources.join(','), condominium, action, days, effectiveSearch, before],
    queryFn: () => {
      const params = new URLSearchParams();
      if (sources.length > 0) params.set('sources', sources.join(','));
      if (condominium) params.set('condominium', condominium);
      if (action) params.set('action', action);
      if (days) params.set('days', days);
      if (effectiveSearch) params.set('search', effectiveSearch);
      if (before) params.set('before', before);
      return api.get<LogsResponse>(`/admin/logs?${params.toString()}`);
    },
    // As opcoes de filtro so viajam na primeira pagina; sem isto, paginar
    // esvaziaria os selects.
    placeholderData: (previous) => previous,
  });

  const [options, setOptions] = useState<SystemLogFilterOptions | null>(null);
  if (data?.filters && data.filters !== options) setOptions(data.filters);

  const items = data?.items ?? [];

  /**
   * As acoes disponiveis seguem as origens marcadas.
   *
   * Deixar "Encomenda entregue" no filtro quando so a trilha da Azko esta ligada
   * produziria uma lista vazia e a impressao de que o sistema perdeu registro.
   */
  const actionOptions = useMemo(() => {
    const active = sources.length > 0 ? sources : [...LOG_SOURCES];
    const list: Array<{ value: string; label: string }> = [];

    if (active.includes('plataforma')) {
      for (const item of options?.platformActions ?? []) {
        list.push({ value: item, label: `Azko · ${describeLogAction('plataforma', item)}` });
      }
    }
    if (active.includes('condominio')) {
      for (const item of options?.condominiumActions ?? []) {
        list.push({ value: item, label: `Condominio · ${describeLogAction('condominio', item)}` });
      }
    }
    if (active.includes('encomenda')) {
      for (const type of PACKAGE_EVENT_TYPES) {
        list.push({
          value: `encomenda.${type}`,
          label: `Encomenda · ${describeLogAction('encomenda', `encomenda.${type}`)}`,
        });
      }
    }

    return list;
  }, [options, sources]);

  function toggleSource(source: LogSource) {
    setPages([]);
    setAction('');
    setSources((current) =>
      current.includes(source) ? current.filter((item) => item !== source) : [...current, source],
    );
  }

  const filtering =
    sources.length > 0 || Boolean(condominium) || Boolean(action) || Boolean(effectiveSearch);

  return (
    <>
      <div>
        <p className="eyebrow">Plataforma</p>
        <h1 className="mt-2 text-2xl font-extrabold tracking-tight sm:text-3xl">Logs.</h1>
        <p className="mt-2 text-sm text-muted">
          Tudo o que acontece no sistema: as acoes da equipe Azko, o que cada sindico faz no predio
          dele e a vida de cada encomenda. O log da Azko e imutavel — o banco recusa alteracao e
          exclusao, nem administrador reescreve.
        </p>
      </div>

      <Card className="p-4 sm:p-5">
        {/* Origem primeiro, e como botao e nao select: e o filtro que muda o
            SIGNIFICADO da lista, e precisa mostrar o estado sem abrir nada. */}
        <div className="flex flex-wrap gap-2">
          {LOG_SOURCES.map((source) => {
            const on = sources.includes(source);
            return (
              <button
                key={source}
                type="button"
                onClick={() => toggleSource(source)}
                title={LOG_SOURCE_DESCRIPTIONS[source]}
                aria-pressed={on}
                className={cx(
                  // Alvo de toque: py-2 no celular passa dos 40px de altura. O py-1.5 do
                  // desktop dava 32px — abaixo do que um polegar acerta sem tentar duas vezes.
                  'rounded-pill border px-4 py-2 text-sm font-semibold transition sm:px-3 sm:py-1.5',
                  on
                    ? 'border-accent bg-accent/15 text-accent'
                    : 'border-line text-muted hover:border-line-strong hover:text-fg',
                )}
              >
                {LOG_SOURCE_LABELS[source]}
              </button>
            );
          })}
          {sources.length > 0 && (
            <Button
              variant="quiet"
              className="py-1.5"
              onClick={() => {
                setSources([]);
                setAction('');
                setPages([]);
              }}
            >
              Todas as origens
            </Button>
          )}
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            value={condominium}
            onChange={reset(setCondominium)}
            emptyOption="Todos os condominios"
            options={(options?.condominiums ?? []).map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            aria-label="Filtrar por condominio"
          />

          <Select
            value={action}
            onChange={reset(setAction)}
            emptyOption="Todas as acoes"
            options={actionOptions}
            aria-label="Filtrar por acao"
          />

          <Select
            value={days}
            onChange={reset(setDays)}
            options={PERIODS}
            aria-label="Periodo"
          />

          <SearchInput
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPages([]);
            }}
            placeholder="Quem fez, ou o alvo"
            aria-label="Buscar"
          />
        </div>
      </Card>

      {isLoading && <LoadingRows rows={6} />}

      {!isLoading && items.length === 0 && (
        <EmptyState
          title="Nada neste recorte"
          description={
            filtering
              ? 'Nenhum registro combina com os filtros. Tente ampliar o periodo ou limpar a origem.'
              : 'Nenhum registro no periodo escolhido. Aumente a janela para ver o que veio antes.'
          }
        />
      )}

      {items.length > 0 && (
        <Card className="overflow-hidden p-0">
          <LogFeed
            entries={items}
            showSource
            showCondominium
            footer={
              <div className="flex flex-wrap items-center gap-3 border-t border-line-soft px-4 py-4 sm:px-6">
                {pages.length > 0 && (
                  <Button variant="ghost" onClick={() => setPages((p) => p.slice(0, -1))}>
                    Voltar
                  </Button>
                )}
                {data?.nextCursor && (
                  <Button
                    variant="ghost"
                    loading={isFetching}
                    onClick={() => setPages((p) => [...p, data.nextCursor as string])}
                  >
                    Mais antigos
                  </Button>
                )}
                {pages.length > 0 && (
                  <Button variant="quiet" onClick={() => setPages([])}>
                    Voltar ao inicio
                  </Button>
                )}
              </div>
            }
          />
        </Card>
      )}
    </>
  );
}
